import { Button, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { ALLOWED_JOB_STATUSES, type JobStatus } from './contractsMirror.js';
import { useCreateJobMutation } from './useJobQuery.js';

/**
 * The real create-job form (UI-SPEC archetype E — grouped sections). Wired to
 * `POST /api/v1/jobs` through `useCreateJobMutation`; the server holds the Admin/Manager gate,
 * normalizes + uniqueness-checks the job code, validates commission/retainage percents and the
 * status enum, and resolves the tenant from the principal (the SPA never sends a tenant id —
 * SEC-11). A duplicate job code surfaces as an INLINE field error (the server's additive
 * `job_code_exists` code, 409). The edit form lives in `JobEditRoute`.
 *
 * Only `mode === 'create'` renders the live form; any other mode keeps the informative notice so
 * the component never silently no-ops.
 */

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const TEXTAREA_CLASS =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';
const SECTION_LEGEND_CLASS =
  'mb-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide';

export function JobForm({ mode }: { mode: 'create' | 'edit' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = useCreateJobMutation();

  const [jobName, setJobName] = useState('');
  const [jobCode, setJobCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [soldBy, setSoldBy] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [status, setStatus] = useState<JobStatus>('Active');
  const [isOverhead, setIsOverhead] = useState(false);
  const [contractAmount, setContractAmount] = useState('');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [retainagePercent, setRetainagePercent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [jobCodeError, setJobCodeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // The route body already branches on `id === 'new'`; a non-create mode keeps the notice.
  if (mode !== 'create') {
    return (
      <div className="min-h-full bg-background px-6 py-8">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('jobs:form.editHeading')}
          </h1>
          <Button asChild variant="secondary">
            <Link to="/jobs">{t('jobs:actions.back')}</Link>
          </Button>
        </header>
        <div className="max-w-xl rounded-md border border-border bg-card p-6">
          <p className="font-medium text-foreground text-sm">{t('jobs:form.unavailable')}</p>
          <p className="mt-2 text-muted-foreground text-sm">{t('jobs:form.unavailableBody')}</p>
        </div>
      </div>
    );
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setJobCodeError(null);
    setFormError(null);

    createMutation.mutate(
      {
        jobName: jobName.trim(),
        jobCode: jobCode.trim() || null,
        clientName: clientName.trim() || null,
        soldBy: soldBy.trim() || null,
        jobDescription: jobDescription.trim() || null,
        status,
        isOverhead,
        contractAmount: contractAmount.trim() ? Number(contractAmount) : null,
        commissionPercent: commissionPercent.trim() ? Number(commissionPercent) : null,
        retainagePercent: retainagePercent.trim() ? Number(retainagePercent) : null,
        startDate: startDate || null,
      },
      {
        onSuccess: (result) => {
          const newId = result.jobId;
          if (newId) {
            void navigate({ to: '/jobs/$id', params: { id: String(newId) } });
          } else {
            void navigate({ to: '/jobs' });
          }
        },
        onError: (error) => {
          if (error instanceof ApiError && error.message === 'job_code_exists') {
            setJobCodeError(t('jobs:form.jobCodeExists'));
            return;
          }
          setFormError(t('jobs:form.saveError'));
        },
      },
    );
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t('jobs:form.createHeading')}
        </h1>
        <Button asChild variant="secondary">
          <Link to="/jobs">{t('jobs:actions.back')}</Link>
        </Button>
      </header>

      <form
        onSubmit={submit}
        className="max-w-3xl space-y-8 rounded-md border border-border bg-card p-6"
      >
        <fieldset>
          <legend className={SECTION_LEGEND_CLASS}>{t('jobs:form.sections.details')}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="job-name" className={LABEL_CLASS}>
                {t('jobs:form.jobName')}
              </label>
              <input
                id="job-name"
                required
                value={jobName}
                onChange={(event) => setJobName(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="job-code" className={LABEL_CLASS}>
                {t('jobs:form.jobCode')}
              </label>
              <input
                id="job-code"
                value={jobCode}
                onChange={(event) => setJobCode(event.target.value)}
                aria-invalid={jobCodeError ? true : undefined}
                aria-describedby={jobCodeError ? 'job-code-error' : undefined}
                className={INPUT_CLASS}
              />
              {jobCodeError ? (
                <p id="job-code-error" role="alert" className="mt-1 text-destructive text-sm">
                  {jobCodeError}
                </p>
              ) : null}
            </div>

            <div>
              <label htmlFor="job-status" className={LABEL_CLASS}>
                {t('jobs:form.status')}
              </label>
              <select
                id="job-status"
                value={status}
                onChange={(event) => setStatus(event.target.value as JobStatus)}
                className={INPUT_CLASS}
              >
                {ALLOWED_JOB_STATUSES.map((statusOption) => (
                  <option key={statusOption} value={statusOption}>
                    {t(`jobs:status.${statusKey(statusOption)}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="job-client-name" className={LABEL_CLASS}>
                {t('jobs:form.clientName')}
              </label>
              <input
                id="job-client-name"
                value={clientName}
                onChange={(event) => setClientName(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="job-start-date" className={LABEL_CLASS}>
                {t('jobs:form.startDate')}
              </label>
              <input
                id="job-start-date"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="job-description" className={LABEL_CLASS}>
                {t('jobs:form.jobDescription')}
              </label>
              <textarea
                id="job-description"
                value={jobDescription}
                onChange={(event) => setJobDescription(event.target.value)}
                rows={4}
                className={TEXTAREA_CLASS}
              />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend className={SECTION_LEGEND_CLASS}>{t('jobs:form.sections.financials')}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="job-contract-amount" className={LABEL_CLASS}>
                {t('jobs:form.contractAmount')}
              </label>
              <input
                id="job-contract-amount"
                type="number"
                min="0"
                step="0.01"
                value={contractAmount}
                onChange={(event) => setContractAmount(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="job-sold-by" className={LABEL_CLASS}>
                {t('jobs:form.soldBy')}
              </label>
              <input
                id="job-sold-by"
                value={soldBy}
                onChange={(event) => setSoldBy(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="job-commission" className={LABEL_CLASS}>
                {t('jobs:form.commissionPercent')}
              </label>
              <input
                id="job-commission"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={commissionPercent}
                onChange={(event) => setCommissionPercent(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="job-retainage" className={LABEL_CLASS}>
                {t('jobs:form.retainagePercent')}
              </label>
              <input
                id="job-retainage"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={retainagePercent}
                onChange={(event) => setRetainagePercent(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="inline-flex items-center gap-2 font-medium text-foreground text-sm">
                <input
                  type="checkbox"
                  checked={isOverhead}
                  onChange={(event) => setIsOverhead(event.target.checked)}
                  className="size-4 rounded border border-border bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                />
                {t('jobs:form.isOverhead')}
              </label>
            </div>
          </div>
        </fieldset>

        {formError ? (
          <p role="alert" className="text-destructive text-sm">
            {formError}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={createMutation.isPending}>
            {t('jobs:form.submit')}
          </Button>
          <Button asChild variant="secondary">
            <Link to="/jobs">{t('jobs:form.cancel')}</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Map a status label to its i18n key segment ("On Hold" → "onHold"). */
function statusKey(status: JobStatus): string {
  return status === 'On Hold' ? 'onHold' : status.toLowerCase();
}
